import { getOzowApiUrl } from "src/utils";
import {
  Bank,
  GetBanksParams,
  InitiateDepositPayload,
  InitiateDepositResponse,
  InitiateWithdrawalPayload,
  InitiateWithdrawalResponse,
} from "./types";

export enum OzowEndpoints {
  INITIATE_DEPOSIT = "/ozow/initiate-deposit",
  INITIATE_WITHDRAWAL = "/ozow/initiate-withdrawal",
  GET_BANKS_LIST = "/ozow/get-banks",
}

class OzowClient {
  private apiUrl: string;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  /**
   * Execute a REST request
   */
  private async request<T = any>(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    endpoint: string,
    options?: {
      body?: Record<string, any>;
      params?: Record<string, string>;
    }
  ): Promise<T> {
    try {
      // Add query parameters for GET requests
      const queryString = options?.params ? "?" + new URLSearchParams(options.params).toString() : "";

      const response = await fetch(`${this.apiUrl.replace("/graphql", "")}${endpoint}${queryString}`, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.warn(`Request failed: ${response.status} ${response.statusText}. ${errorBody}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to execute REST request:", error);
      throw error;
    }
  }

  /**
   * Start an Ozow deposit session and record the payment
   * @param payload - Deposit initialization data including exchange, session, URLs, and customer info
   * @returns Response with redirect URL or error details
   */
  public async initiateDeposit(payload: InitiateDepositPayload): Promise<InitiateDepositResponse> {
    return this.request<InitiateDepositResponse>("POST", OzowEndpoints.INITIATE_DEPOSIT, {
      body: payload,
    });
  }

  /**
   * Request an Ozow payout and store it locally
   * @param payload - Withdrawal initialization data including amount, bank details, and RTC flag
   * @returns Response with confirmation message
   */
  public async initiateWithdrawal(payload: InitiateWithdrawalPayload): Promise<InitiateWithdrawalResponse> {
    return this.request<InitiateWithdrawalResponse>("POST", OzowEndpoints.INITIATE_WITHDRAWAL, {
      body: payload,
    });
  }

  /**
   * Retrieve Ozow's list of available banks
   * @param params - Query parameters including exchange and optional rtconly flag
   * @returns Array of available banks
   */
  public async getBanksList(params: GetBanksParams): Promise<Bank[]> {
    return this.request<Bank[]>("GET", OzowEndpoints.GET_BANKS_LIST, {
      params: {
        exchange: params.exchange,
        ...(params.rtconly !== undefined && { rtconly: String(params.rtconly) }),
      },
    });
  }
}

export const ozowService = new OzowClient(getOzowApiUrl());
